'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { motion, Variants } from 'framer-motion';

import { Button } from '../../ui/Button';
import { Icon } from '../../ui/Icon';
import LanguageSelector, { Language } from '@/components/ui/LanguageSelector';
import { MenuItem } from '@/lib/drupal';
import { trackEvent } from '@/lib/analytics';

const headerItem: Variants = {
  hidden: { opacity: 0, y: -8 },
  visible: { opacity: 1, y: 0 },
};

interface HeaderContentProps {
  menuItems: MenuItem[];
  socialLinks: MenuItem[];
  languages: Language[];
  children: React.ReactNode;
}

export default function HeaderContent({
  menuItems,
  socialLinks,
  languages,
  children,
}: HeaderContentProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        toggleButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !mobileMenuRef.current) return;

    const menu = mobileMenuRef.current;
    const focusableSelector = 'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])';

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusableElements = menu.querySelectorAll<HTMLElement>(focusableSelector);
      if (focusableElements.length === 0) return;

      const firstEl = focusableElements[0];
      const lastEl = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } 
      else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };

    document.addEventListener('keydown', handleTab);

    const timer = setTimeout(() => {
      const firstFocusable = menu.querySelector<HTMLElement>(focusableSelector);
      firstFocusable?.focus();
    }, 500);

    return () => {
      document.removeEventListener('keydown', handleTab);
      clearTimeout(timer);
    };
  }, [isOpen]);

  return (
    <header
      className={`
        fixed top-0 left-0 w-full z-50 bg-white transition-all duration-500
        ${isScrolled ? 'shadow-md py-4' : 'py-5'}
      `}
    >
      <div className="header-container flex items-center justify-between relative">
        {/* Logo */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={headerItem}
          transition={{ duration: 0.5, ease: [0.21, 0.47, 0.32, 0.98] }}
        >
          {children}
        </motion.div>

        {/* DESKTOP MENU */}
        <motion.nav
          aria-label="Main navigation"
          className="hidden lg:flex items-center gap-6"
          initial="hidden"
          animate="visible"
          transition={{ staggerChildren: 0.06, delayChildren: 0.15 }}
        >
          {menuItems.map((item) => (
            <motion.div key={item.title} variants={headerItem} transition={{ duration: 0.4, ease: [0.21, 0.47, 0.32, 0.98] }}>
              <Link
                href={item.url}
                className="text-brand-primary text-lg font-medium hover:text-brand-secondary transition-colors"
              >
                {item.title}
              </Link>
            </motion.div>
          ))}

          <motion.div variants={headerItem} transition={{ duration: 0.4, ease: [0.21, 0.47, 0.32, 0.98] }}>
            <LanguageSelector variant="header" languages={languages} />
          </motion.div>
        </motion.nav>

        {/* MOBILE BURGER TOGGLE */}
        <button
          ref={toggleButtonRef}
          className="lg:hidden relative z-[70] text-brand-primary p-2 transition-transform duration-300"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isOpen}
          aria-controls="mobile-menu"
        >
          <div className="w-8 h-6 flex flex-col justify-between items-end" aria-hidden="true">
            <span className={`h-0.5 bg-brand-primary transition-all duration-300 ${isOpen ? 'w-8 translate-y-2.5 -rotate-45' : 'w-8'}`} />
            <span className={`h-0.5 bg-brand-primary transition-all duration-300 ${isOpen ? 'opacity-0' : 'w-6'}`} />
            <span className={`h-0.5 bg-brand-primary transition-all duration-300 ${isOpen ? 'w-8 -translate-y-3 rotate-45' : 'w-4'}`} />
          </div>
        </button>

        {/* MOBILE MENU BOX */}
        <div
          id="mobile-menu"
          role="dialog"
          aria-modal={isOpen ? true : undefined}
          aria-label="Mobile navigation"
          className={`
            fixed inset-0 z-[60] lg:hidden transition-all duration-700 ease-[cubic-bezier(0.77,0,0.175,1)]
            ${isOpen ? 'visible' : 'invisible'}
          `}
        >
          <div
            className={`
              absolute inset-0 bg-brand-primary/5 backdrop-blur-sm transition-opacity duration-500
              ${isOpen ? 'opacity-100' : 'opacity-0'}
            `}
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          ></div>

          <div
            ref={mobileMenuRef}
            className={`
              absolute top-0 right-0 w-[85%] max-w-100 h-full bg-white shadow-2xl flex flex-col transition-transform duration-500 ease-out
              ${isOpen ? 'translate-x-0' : 'translate-x-full'}
            `}
          >
            <div className="flex-1 flex flex-col p-10 pt-24 overflow-y-auto">
              <nav aria-label="Mobile navigation" className="flex flex-col gap-8">
                {menuItems.map((item, index) => (
                  <Link
                    key={item.title}
                    href={item.url}
                    onClick={() => setIsOpen(false)}
                    className={`
                      text-xl font-medium text-brand-primary hover:text-brand-secondary transition-all duration-500
                      ${isOpen ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'}
                    `}
                    style={{ transitionDelay: `${isOpen ? 300 + (index * 100) : 0}ms` }}
                  >
                    {item.title}
                  </Link>
                ))}
              </nav>

              <div className={`mt-12 pt-12 border-t border-brand-primary/5 transition-all duration-700 delay-[800ms] ${
                isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}>
                <div className="flex flex-col gap-6">
                  <LanguageSelector variant="mobile" languages={languages} />

                  <div className="mt-4 text-center">
                    <p className="text-xs uppercase tracking-widest text-brand-primary/40 font-bold">Get in touch</p>
                    <a href="mailto:hello@agathos.tech" className="block mt-2.5 text-lg font-medium text-brand-primary hover:underline">hello@agathos.tech</a>
                  </div>

                  <div className="flex justify-center items-center gap-4 mt-4" role="list" aria-label="Social links">
                    {socialLinks.map((social) => (
                      <Link
                        key={social.title}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        role="listitem"
                        aria-label={`${social.title} (opens in new tab)`}
                        className="text-sm lg:text-[15px] text-brand-primary hover:text-brand-secondary transition-colors"
                        onClick={() => trackEvent('social_click', {
                          platform: social.title,
                          link_url: social.url,
                        })}
                      >
                        <Icon name={social.title as keyof typeof import('lucide-react')} />
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              <div className={`p-10 transition-all duration-700 delay-[1000ms] ${
                isOpen ? 'opacity-100' : 'opacity-0'
              }`}>
                <Button
                  label="Let's Talk"
                  href="/contact"
                  onClick={() => trackEvent('cta_click', {
                    cta_label: "Let's Talk",
                    cta_location: 'mobile_menu',
                  })}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
